import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Eye, Trash2, Star, ClipboardList,
  ThumbsUp, ThumbsDown, MessageSquare, Copy
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SurveyManagement = ({ token, language }) => {
  const [surveys, setSurveys] = useState([]);
  const [surveyStats, setSurveyStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [surveyFilter, setSurveyFilter] = useState("all");
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [isSurveysLoading, setIsSurveysLoading] = useState(false);

  const fetchSurveys = async () => {
    if (!token) return;
    setIsSurveysLoading(true);
    try {
      const response = await axios.get(`${API}/admin/surveys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSurveys(response.data.surveys);
      setSurveyStats(response.data.stats);
    } catch (error) {
      console.error("Error fetching surveys:", error);
    } finally {
      setIsSurveysLoading(false);
    }
  };

  useEffect(() => {
    fetchSurveys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleUpdateSurvey = async (surveyId, updateData) => {
    try {
      await axios.put(
        `${API}/admin/surveys/${surveyId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(language === 'es' ? "Encuesta actualizada" : "Survey updated");
      fetchSurveys();
      setSelectedSurvey(null);
    } catch (error) {
      toast.error(language === 'es' ? "Error al actualizar encuesta" : "Error updating survey");
    }
  };

  const handleDeleteSurvey = async (surveyId) => {
    if (!window.confirm(language === 'es' ? "¿Eliminar esta encuesta?" : "Delete this survey?")) return;
    try {
      await axios.delete(`${API}/admin/surveys/${surveyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'es' ? "Encuesta eliminada" : "Survey deleted");
      fetchSurveys();
    } catch (error) {
      toast.error(language === 'es' ? "Error al eliminar" : "Error deleting");
    }
  };

  const copySurveyLink = () => {
    const link = `${window.location.origin}/encuesta`;
    navigator.clipboard.writeText(link);
    toast.success(language === 'es' ? "Enlace copiado" : "Link copied");
  };

  const filteredSurveys = surveys.filter(s => surveyFilter === "all" || s.status === surveyFilter);

  const statusBadge = (status) => {
    const cls = status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                status === 'rejected' ? 'bg-red-100 text-red-700' :
                'bg-amber-100 text-amber-700';
    const label = status === 'approved' ? (language === 'es' ? 'Aprobada' : 'Approved') :
                  status === 'rejected' ? (language === 'es' ? 'Rechazada' : 'Rejected') :
                  (language === 'es' ? 'Pendiente' : 'Pending');
    return <Badge className={cls}>{label}</Badge>;
  };

  return (
    <>
      <Card className="mb-8 border-amber-200/50" data-testid="survey-management-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-50 rounded-lg">
                <ClipboardList className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-lg">
                  {language === 'es' ? 'Encuestas de Satisfacción' : 'Satisfaction Surveys'}
                </CardTitle>
                <CardDescription>
                  {language === 'es' ? 'Gestiona y aprueba las encuestas recibidas' : 'Manage and approve received surveys'}
                </CardDescription>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={copySurveyLink} data-testid="copy-survey-link-btn">
              <Copy className="w-4 h-4 mr-1" />
              {language === 'es' ? 'Copiar enlace' : 'Copy link'}
            </Button>
          </div>
          {/* Stats row */}
          <div className="grid grid-cols-4 gap-3 mt-4">
            <div className="p-3 bg-stone-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-stone-800">{surveyStats.total}</p>
              <p className="text-xs text-stone-500">Total</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-amber-600">{surveyStats.pending}</p>
              <p className="text-xs text-amber-600">{language === 'es' ? 'Pendientes' : 'Pending'}</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-emerald-600">{surveyStats.approved}</p>
              <p className="text-xs text-emerald-600">{language === 'es' ? 'Aprobadas' : 'Approved'}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-red-500">{surveyStats.rejected}</p>
              <p className="text-xs text-red-500">{language === 'es' ? 'Rechazadas' : 'Rejected'}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filter tabs */}
          <div className="flex gap-2 mb-4">
            {["all", "pending", "approved", "rejected"].map((filter) => (
              <Button
                key={filter}
                size="sm"
                variant={surveyFilter === filter ? "default" : "outline"}
                onClick={() => setSurveyFilter(filter)}
                className={surveyFilter === filter ? "bg-stone-900" : ""}
                data-testid={`survey-filter-${filter}`}
              >
                {filter === "all" ? (language === 'es' ? 'Todas' : 'All') :
                 filter === "pending" ? (language === 'es' ? 'Pendientes' : 'Pending') :
                 filter === "approved" ? (language === 'es' ? 'Aprobadas' : 'Approved') :
                 (language === 'es' ? 'Rechazadas' : 'Rejected')}
              </Button>
            ))}
          </div>

          {isSurveysLoading ? (
            <div className="text-center py-8 text-stone-500">
              {language === 'es' ? 'Cargando encuestas...' : 'Loading surveys...'}
            </div>
          ) : filteredSurveys.length === 0 ? (
            <div className="text-center py-8 text-stone-400">
              <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p>{language === 'es' ? 'No hay encuestas en esta categoría' : 'No surveys in this category'}</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">{language === 'es' ? 'Cliente' : 'Customer'}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">{language === 'es' ? 'Servicio' : 'Service'}</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-stone-600">{language === 'es' ? 'Satisfacción' : 'Satisfaction'}</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-stone-600">{language === 'es' ? 'Estado' : 'Status'}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-600">{language === 'es' ? 'Fecha' : 'Date'}</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-stone-600">{language === 'es' ? 'Acciones' : 'Actions'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {filteredSurveys.map((survey) => (
                    <tr key={survey.id} className="hover:bg-stone-50" data-testid={`survey-row-${survey.id}`}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-stone-900">{survey.name}</p>
                          <p className="text-xs text-stone-500">{survey.email}</p>
                          <p className="text-xs text-stone-400">{survey.city_state}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary">{survey.service_type}</Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={`star-${survey.id}-${i}`}
                              className={`w-4 h-4 ${i < survey.satisfaction_rating ? 'fill-amber-400 text-amber-400' : 'text-stone-200'}`}
                            />
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {statusBadge(survey.status)}
                      </td>
                      <td className="px-4 py-3 text-sm text-stone-500">
                        {new Date(survey.submitted_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setSelectedSurvey(survey)} data-testid={`survey-view-${survey.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {survey.status !== 'approved' && (
                            <Button
                              size="sm" variant="ghost"
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleUpdateSurvey(survey.id, { status: 'approved', show_in_slider: true })}
                              data-testid={`survey-approve-${survey.id}`}
                            >
                              <ThumbsUp className="w-4 h-4" />
                            </Button>
                          )}
                          {survey.status !== 'rejected' && (
                            <Button
                              size="sm" variant="ghost"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleUpdateSurvey(survey.id, { status: 'rejected', show_in_slider: false })}
                              data-testid={`survey-reject-${survey.id}`}
                            >
                              <ThumbsDown className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm" variant="ghost"
                            className="text-red-400 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteSurvey(survey.id)}
                            data-testid={`survey-delete-${survey.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Survey Detail Dialog */}
      <Dialog open={!!selectedSurvey} onOpenChange={(open) => !open && setSelectedSurvey(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              {language === 'es' ? 'Detalle de Encuesta' : 'Survey Details'}
            </DialogTitle>
          </DialogHeader>
          {selectedSurvey && (
            <div className="space-y-4" data-testid="survey-detail-dialog">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-lg">{selectedSurvey.name}</p>
                  <p className="text-sm text-stone-500">{selectedSurvey.email}</p>
                  <p className="text-sm text-stone-400">{selectedSurvey.city_state}</p>
                </div>
                {statusBadge(selectedSurvey.status)}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 mb-1">{language === 'es' ? 'Servicio' : 'Service'}</p>
                  <p className="font-medium">{selectedSurvey.service_type}</p>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 mb-1">{language === 'es' ? 'Satisfacción' : 'Satisfaction'}</p>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`detail-star-${i}`} className={`w-4 h-4 ${i < selectedSurvey.satisfaction_rating ? 'fill-amber-400 text-amber-400' : 'text-stone-200'}`} />
                    ))}
                  </div>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 mb-1">{language === 'es' ? 'Calidad de impresión' : 'Print quality'}</p>
                  <p className="font-medium">{selectedSurvey.print_quality}</p>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 mb-1">{language === 'es' ? 'Cumplió expectativas' : 'Met expectations'}</p>
                  <p className="font-medium">{selectedSurvey.delivery_met_expectations ? (language === 'es' ? 'Sí' : 'Yes') : 'No'}</p>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg col-span-2">
                  <p className="text-xs text-stone-500 mb-1">{language === 'es' ? '¿Volvería a comprar?' : 'Would buy again?'}</p>
                  <p className="font-medium">{selectedSurvey.would_buy_again ? (language === 'es' ? 'Sí' : 'Yes') : 'No'}</p>
                </div>
              </div>

              {selectedSurvey.improvement_suggestions && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-xs text-stone-500 mb-1">{language === 'es' ? 'Sugerencias de mejora' : 'Improvement suggestions'}</p>
                  <p className="text-sm text-stone-700">{selectedSurvey.improvement_suggestions}</p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                {selectedSurvey.status !== 'approved' && (
                  <Button
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleUpdateSurvey(selectedSurvey.id, { status: 'approved', show_in_slider: true })}
                    data-testid="survey-detail-approve-btn"
                  >
                    <ThumbsUp className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Aprobar' : 'Approve'}
                  </Button>
                )}
                {selectedSurvey.status !== 'rejected' && (
                  <Button
                    variant="outline"
                    className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => handleUpdateSurvey(selectedSurvey.id, { status: 'rejected', show_in_slider: false })}
                    data-testid="survey-detail-reject-btn"
                  >
                    <ThumbsDown className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Rechazar' : 'Reject'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
